// The public page: skills, plugins and prompt templates, listed apart and each can be shown alone. Each entry says what it
// contains. The words follow the chosen language (i18n.ts).

import { ApiFailure, fetchFile, get } from './api.ts';
import { fmtDateIn, isLanguage, setLang, t, type WordKey } from './i18n.ts';
import { topbar } from './layout.ts';
import { openContentsSubmit } from './submit.ts';
import type { EntryKind, ItemInfo, ListEntry, PublicContents, SearchResponse } from './types.ts';
import { esc, fmtSize, iconHtml, loadingBlock, md, must, onAct, refresh, toast } from './util.ts';

type Filter = 'all' | EntryKind;
const KINDS: EntryKind[] = ['skill', 'plugin', 'prompt'];
const ONE: Record<EntryKind, WordKey> = { skill: 'skillOne', plugin: 'pluginOne', prompt: 'promptOne' };

let query = '';
let filter: Filter = 'all';
let listRequest = 0;

const errorHtml = (message: string) => `<div class="empty"><b>${esc(t('loadFailed'))}</b>${esc(message)}<br><br><button class="btn" data-act="reload">${esc(t('retry'))}</button></div>`;

function languageChoice(): void {
  const select = document.querySelector<HTMLSelectElement>('#lang');
  select?.addEventListener('change', () => {
    if (isLanguage(select.value)) setLang(select.value);
    refresh();
  });
}

export function renderList(app: HTMLElement): void {
  const tab = (value: Filter) => `<button class="${filter === value ? 'on' : ''}" data-act="kind-filter" data-kind="${value}" aria-pressed="${filter === value}">${t(value)}</button>`;
  app.innerHTML = `${topbar('market')}<main class="page"><div class="page-head"><div><h1>${t('market')}</h1>
    <p class="sub">${esc(t('intro'))}</p></div>
    <div class="tools"><label class="search"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="7"/><path d="M20 20l-3.5-3.5"/></svg>
    <input id="q" placeholder="${esc(t('search'))}" value="${esc(query)}" autocomplete="off"></label>
    <button class="btn-pill" data-act="submit-package">${t('submit')}</button></div></div>
    <div class="tabs kind-tabs" role="group">${tab('all')}${KINDS.map(tab).join('')}</div>
    <div id="list">${loadingBlock(t('loading'))}</div></main>`;
  languageChoice();
  let timer: number | undefined;
  must<HTMLInputElement>('#q').addEventListener('input', (event) => {
    query = (event.target as HTMLInputElement).value;
    window.clearTimeout(timer);
    timer = window.setTimeout(() => void load(), 250);
  });
  void load();
}

async function load(): Promise<void> {
  const mine = ++listRequest;
  const target = document.querySelector<HTMLElement>('#list');
  if (!target) return;
  const q = query.trim();
  const params = new URLSearchParams();
  if (q) params.set('q', q);
  if (filter !== 'all') params.set('kind', filter);
  try {
    const found = await get<SearchResponse>(`/api/v1/search${params.size ? `?${params}` : ''}`);
    if (mine !== listRequest) return;
    target.innerHTML = listHtml(found.results, q);
  } catch (thrown) {
    if (mine !== listRequest) return;
    target.innerHTML = errorHtml(thrown instanceof ApiFailure ? thrown.message : t('tryLater'));
  }
}

const rowHtml = (entry: ListEntry) => `<a class="row" href="#/items/${encodeURIComponent(entry.id)}">${iconHtml(entry.icon, entry.kind)}<div class="row-text">
  <div class="row-title">${esc(entry.name)}<span class="ver">${esc(entry.version)}</span></div><div class="row-sub">${esc(entry.summary)}</div>
  <div class="row-sub">${contentsLine(entry)}</div></div><span class="row-go">›</span></a>`;

const section = (title: string, entries: ListEntry[]) => `<section class="group"><h2>${esc(title)}</h2><div class="grid">${entries.map(rowHtml).join('')}</div></section>`;

function listHtml(entries: ListEntry[], q: string): string {
  if (entries.length === 0) {
    if (q) return `<div class="empty"><b>${esc(t('noMatch', { q }))}</b>${esc(t('noMatchHint'))}</div>`;
    const title = filter === 'all' ? t('emptyAll') : t('emptyKind', { kind: t(filter) });
    return `<div class="empty"><b>${esc(title)}</b>${esc(t('emptyHint'))}</div>`;
  }
  // All kinds: one section per kind, in the order the search gives. One kind: its entries by page group.
  if (filter === 'all') {
    return KINDS.map((kind) => [kind, entries.filter((entry) => entry.kind === kind)] as const)
      .filter(([, list]) => list.length > 0)
      .map(([kind, list]) => section(t(kind), list))
      .join('');
  }
  // Nothing grouped: the kind is the only heading the list needs.
  if (entries.every((entry) => !entry.category)) return section(t(filter), entries);
  const groups = new Map<string, { title: string; position: number; entries: ListEntry[] }>();
  for (const entry of entries) {
    const key = entry.category?.id ?? '';
    const group = groups.get(key) ?? { title: entry.category?.name ?? t('other'), position: entry.category?.position ?? Number.MAX_SAFE_INTEGER, entries: [] };
    group.entries.push(entry);
    groups.set(key, group);
  }
  return [...groups.values()].sort((a, b) => a.position - b.position).map((group) => section(group.title, group.entries)).join('');
}

function contentsLine(entry: ListEntry): string {
  if (entry.kind === 'prompt') return esc(t('promptLine'));
  const contents: PublicContents | undefined = entry.contents;
  if (!contents) return '';
  const parts: string[] = [];
  if (contents.skills.length > 0) parts.push(t('skillsCount', { n: contents.skills.length }));
  if (contents.services.some((service) => service.account)) parts.push(t('needsAccount'));
  if (contents.services.some((service) => service.transport === 'local')) parts.push(t('runsHere'));
  if (contents.pages.length > 0) parts.push(t('toolPage'));
  if ((contents.appFeatures ?? []).length > 0) parts.push(t('appFeature'));
  if (contents.localRequirements.length > 0) parts.push(t('needsHere', { list: contents.localRequirements.map((item) => item.name).join(t('listSeparator')) }));
  return parts.map((part) => esc(part)).join(' · ');
}

export function renderDetail(app: HTMLElement, id: string): void {
  app.innerHTML = `${topbar('market')}<main class="page narrow"><a class="back" href="#/market">${t('back')}</a><div id="detail">${loadingBlock(t('loading'))}</div></main>`;
  languageChoice();
  void (async () => {
    const target = must('#detail');
    try {
      const item = await get<ItemInfo>(`/api/v1/items/${encodeURIComponent(id)}`);
      target.innerHTML = detailHtml(item);
    } catch (thrown) {
      if (thrown instanceof ApiFailure && thrown.status === 404) {
        target.innerHTML = `<div class="empty"><b>${esc(t('unavailable'))}</b>${esc(t('unavailableHint'))}<br><br><a class="btn" href="#/market">${t('backButton')}</a></div>`;
      } else target.innerHTML = errorHtml(thrown instanceof ApiFailure ? thrown.message : t('tryLater'));
    }
  })();
}

const list = (heading: string, items: string[], last = false) =>
  items.length ? `<div class="dim" style="margin-bottom:6px">${esc(heading)}</div><ul style="margin:0 0 ${last ? 0 : 12}px;padding-left:18px;font-size:14px">${items.join('')}</ul>` : '';

function holdsHtml(item: ItemInfo): string {
  const contents = item.contents;
  const skills = contents?.skills ?? [];
  const services = contents?.services ?? [];
  const pages = contents?.pages ?? [];
  const features = contents?.appFeatures ?? [];
  const requirements = contents?.localRequirements ?? [];
  // Pages, App features and requirements are said in the chosen language; their presence is all the page needs from the market.
  const requirement = (name: string, detail: string) => `<li>${esc(name)}<div class="dim">${esc(name === 'FFmpeg' ? t('ffmpegDetail') : detail)}</div></li>`;
  return `<div class="card"><h3>${t('holds')}</h3>
    ${list(t('holdsSkills'), skills.map((skill) => `<li>${esc(skill.name)}${skill.description ? `<div class="dim">${esc(skill.description)}</div>` : ''}</li>`))}
    ${list(t('holdsServices'), services.map((service) => `<li>${esc(service.name)}<div class="dim">${esc(service.account ? t('serviceAccount') : t('serviceLocal'))}</div></li>`))}
    ${list(t('holdsPages'), pages.length ? [`<li>${esc(t('pageSummary'))}</li>`] : [])}
    ${list(t('holdsFeatures'), features.length ? [`<li>${esc(t('featureSummary'))}</li>`] : [])}
    ${list(t('holdsNeeds'), requirements.map((entry) => requirement(entry.name, entry.detail)), true)}
    ${!skills.length && !services.length && !pages.length && !features.length ? `<p class="dim" style="margin:0">${esc(t('holdsNothing'))}</p>` : ''}
    ${contents?.chatGptApps?.length ? `<p class="dim" style="margin:12px 0 0">${esc(t('chatGptNote'))}</p>` : ''}
    </div>`;
}

function detailHtml(item: ItemInfo): string {
  const main = item.prompt
    ? `<article class="card"><h3>${t('promptText')}</h3><pre class="prompt-text">${esc(item.prompt.text)}</pre><p class="dim" style="margin:12px 0 0">${esc(t('promptNote'))}</p></article>`
    : item.body.trim()
      ? `<article class="card md">${md(item.body)}</article>`
      : `<article class="card"><p class="dim" style="margin:0">${esc(t('noDescription'))}</p></article>`;
  return `<div class="detail-head">${iconHtml(item.icon, item.kind, 'lg')}<div class="grow"><h1>${esc(item.name)}<span class="kind-label">${t(ONE[item.kind])}</span></h1><div class="sub" style="margin-top:4px">${esc(item.summary)}</div>
      <div class="meta"><span>${esc(t('version', { v: item.version }))}</span>${item.category ? `<span>·</span><span>${esc(item.category.name)}</span>` : ''}<span>·</span><span>${esc(t('published', { date: fmtDateIn(item.publishedAt) }))}</span></div></div>
      <button class="btn-primary" id="download" data-act="download" data-id="${esc(item.id)}" style="height:44px;padding:0 22px;border-radius:12px">${esc(t('download', { v: item.version }))}</button></div>
    <div class="detail-grid">${main}
      <aside><div class="card"><h3>${t('source')}</h3><dl class="kv"><dt>${t('repository')}</dt><dd><a class="link" href="${esc(item.source.url)}" target="_blank" rel="noopener noreferrer">github.com/${esc(item.source.repo)}</a></dd>
        ${item.source.path ? `<dt>${t('folder')}</dt><dd>${esc(item.source.path)}</dd>` : ''}<dt>${t('commit')}</dt><dd class="mono">${esc(item.source.commit.slice(0, 7))}</dd></dl></div>
      ${item.kind === 'prompt' ? '' : holdsHtml(item)}
      <div class="card"><h3>${t('file')}</h3><dl class="kv"><dt>${t('fileName')}</dt><dd>${esc(item.package.file)}</dd><dt>${t('fileSize')}</dt><dd>${fmtSize(item.package.size)}</dd>
        <dt>${t('checksum')}</dt><dd class="mono" style="font-size:11.5px">${esc(item.package.sha256)}</dd></dl>
        <p class="dim" style="margin:12px 0 0">${esc(t('signedNote'))}</p></div></aside></div>`;
}

onAct('kind-filter', (element) => {
  const value = element.dataset.kind;
  if (value !== 'all' && value !== 'skill' && value !== 'plugin' && value !== 'prompt') return;
  filter = value;
  for (const button of document.querySelectorAll<HTMLButtonElement>('[data-act="kind-filter"]')) {
    const on = button.dataset.kind === value;
    button.classList.toggle('on', on);
    button.setAttribute('aria-pressed', String(on));
  }
  void load();
});

onAct('download', async (element) => {
  const id = element.dataset.id ?? '';
  const button = element as HTMLButtonElement;
  const label = button.textContent;
  button.disabled = true;
  button.innerHTML = `<span class="spin"></span> ${esc(t('downloading'))}`;
  try {
    const { blob, name } = await fetchFile(`/api/v1/items/${encodeURIComponent(id)}/download`);
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = name;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(link.href), 2000);
    toast(t('downloadStarted', { name }));
  } catch (thrown) {
    toast(thrown instanceof ApiFailure ? thrown.message : t('downloadFailed'));
    if (thrown instanceof ApiFailure && thrown.status === 404) refresh();
  } finally {
    button.disabled = false;
    if (label) button.textContent = label;
  }
});

onAct('submit-package', () => openContentsSubmit('user'));
